/**
 * OptimizedImage - Progressive image loading with blur placeholder
 * 
 * PERFORMANCE FIX (December 2025):
 * - Added explicit width/height to prevent CLS (Layout Shift)
 * - Added aspect-ratio CSS for responsive images
 * - Added fetchpriority for LCP images
 * - Improved lazy loading with better rootMargin
 * - Integrated Supabase image CDN transformations for WebP/resizing
 * 
 * Features:
 * - Intersection Observer for true lazy loading
 * - Low-quality image placeholder (LQIP) with blur
 * - Smooth fade-in transition when loaded
 * - Fallback for failed images
 * - Explicit dimensions to prevent CLS
 * - Automatic WebP conversion via Supabase CDN
 * - Responsive srcset generation
 */

import React, { useState, useRef, useEffect, memo, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { 
  getOptimizedImageUrl, 
  generateSrcSet, 
  getImageSizes,
  isSupabaseStorageUrl,
  type QualityPreset 
} from '@/utils/imageOptimization';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  placeholderColor?: string;
  /** Explicit width to prevent CLS - critical for performance */
  width?: number;
  /** Explicit height to prevent CLS - critical for performance */
  height?: number;
  /** Priority loading for LCP images (above-fold) */
  priority?: boolean;
  /** Aspect ratio for responsive sizing (e.g., "1/1", "4/3", "16/9") */
  aspectRatio?: string;
  /** Layout type for responsive srcset sizes */
  layout?: 'card' | 'hero' | 'thumbnail' | 'full' | 'grid';
  /** Quality preset for CDN optimization */
  quality?: QualityPreset;
  /** Disable CDN optimization (use original URL) */
  disableCdn?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

// Generate a tiny SVG placeholder with the dominant color
const generatePlaceholder = (color: string = '#e5e7eb') => {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3Crect fill='${encodeURIComponent(color)}' width='1' height='1'/%3E%3C/svg%3E`;
};

export const OptimizedImage = memo(function OptimizedImage({
  src,
  alt,
  className,
  containerClassName,
  placeholderColor = '#e5e7eb',
  width,
  height,
  priority = false,
  aspectRatio,
  layout = 'card',
  quality,
  disableCdn = false,
  onLoad,
  onError,
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const [isInView, setIsInView] = useState(priority); // Priority images load immediately
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Memoize optimized image URL and srcset
  const optimizedSrc = useMemo(() => {
    if (disableCdn || !isSupabaseStorageUrl(src)) return src;
    
    const targetWidth = width || (
      layout === 'thumbnail' ? 160 :
      layout === 'hero' ? 1280 :
      layout === 'grid' ? 400 :
      640
    );
    
    return getOptimizedImageUrl(src, {
      width: targetWidth,
      format: 'webp',
      quality: quality || (priority ? 'high' : 'medium'),
    });
  }, [src, width, layout, quality, priority, disableCdn]);

  const srcSet = useMemo(() => {
    if (disableCdn || !isSupabaseStorageUrl(src) || useFallback) return undefined;
    
    const maxWidth = width ? width * 2 : (
      layout === 'thumbnail' ? 320 :
      layout === 'hero' ? 2560 :
      1280
    );
    
    return generateSrcSet(src, { maxWidth, format: 'webp' });
  }, [src, width, layout, disableCdn, useFallback]);

  const sizes = useMemo(() => {
    if (disableCdn || !isSupabaseStorageUrl(src) || useFallback) return undefined;
    return getImageSizes(layout);
  }, [src, layout, disableCdn, useFallback]);

  // Intersection Observer for lazy loading (skip for priority images)
  useEffect(() => {
    if (priority) return; // Priority images skip lazy loading
    
    const container = containerRef.current;
    if (!container) return;

    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(container);
          }
        });
      },
      {
        rootMargin: '300px', // Start loading 300px before viewport (increased for better UX)
        threshold: 0,
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    // If we were using the optimized URL and it failed, try the original
    if (!useFallback && optimizedSrc !== src) {
      setUseFallback(true);
      // Reset error state to try again
      setIsError(false);
      return;
    }

    setIsError(true);
    onError?.();
  };

  // Container style with aspect ratio to prevent CLS
  const containerStyle: React.CSSProperties = {
    ...(aspectRatio && { aspectRatio }),
    ...(width && height && !aspectRatio && { aspectRatio: `${width}/${height}` }),
  };

  // Determine which source to use
  const currentSrc = useFallback ? src : optimizedSrc;

  return (
    <div
      ref={containerRef}
      className={cn('relative overflow-hidden bg-gray-100', containerClassName)}
      style={containerStyle}
    >
      {/* Placeholder - shows immediately, prevents CLS */}
      <div
        className={cn(
          'absolute inset-0 transition-opacity duration-300',
          isLoaded ? 'opacity-0' : 'opacity-100'
        )}
        style={{ backgroundColor: placeholderColor }}
        aria-hidden="true"
      />

      {/* Actual image - only load when in view or priority */}
      {isInView && !isError && (
        <img
          ref={imgRef}
          src={currentSrc}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          // @ts-ignore - React warning suggests lowercase, though types might expect camelCase
          fetchpriority={priority ? 'high' : 'auto'}
          className={cn(
            'transition-opacity duration-300',
            isLoaded ? 'opacity-100' : 'opacity-0',
            className
          )}
        />
      )}

      {/* Error state */}
      {isError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <span className="text-gray-400 text-xs">⚠</span>
        </div>
      )}
    </div>
  );
});
